// Inventory service - API integration with localStorage cache

import api from '../utils/api.js';

const STORAGE_KEY = 'inventory:state';
const MAX_SLOTS = 24; // 4x6 grid

// Default state structure
const defaultState = {
  slots: Array(MAX_SLOTS).fill(null), // Array of slot objects
  maxWeight: 50.0,
  currentWeight: 0.0,
  equipped: {
    weapon: null,
    armor: null,
    accessory: null
  }
};

class InventoryService {
  constructor() {
    this.state = this.loadState();
    // Skip default initialization when using API
  }

  // Load state from localStorage
  loadState() {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        return {
          ...defaultState,
          ...parsed
        };
      }
    } catch (error) {
      console.error('Failed to load inventory state:', error);
    }
    return { ...defaultState };
  }

  // Save state to localStorage
  saveState() {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(this.state));
      // Emit global event for state synchronization
      this.emitStateChanged();
    } catch (error) {
      console.error('Failed to save inventory state:', error);
    }
  }

  // Emit global state change event
  emitStateChanged() {
    try {
      window.dispatchEvent(new CustomEvent('inventory:state-changed', {
        detail: {
          currentWeight: this.state.currentWeight,
          maxWeight: this.state.maxWeight,
          slotsUsed: this.state.slots.filter(slot => slot !== null).length,
          totalSlots: MAX_SLOTS,
          timestamp: Date.now()
        }
      }));
    } catch (error) {
      console.warn('Failed to emit inventory state change event:', error);
    }
  }

  // Initialize default items if inventory is empty (disabled for API)
  initializeDefaultItems() {
    console.log('Inventory initialization skipped - using API');
  }

  // Get all inventory from API
  async getInventory() {
    try {
      const response = await api.getInventory();
      const inventory = response.inventory || [];
      
      // Convert API format to local format
      const slots = Array(MAX_SLOTS).fill(null);
      inventory.forEach((item, index) => {
        if (index < MAX_SLOTS) {
          slots[index] = {
            itemId: item.item.id,
            quantity: item.quantity,
            item: item.item
          };
        }
      });
      
      this.state.slots = slots;
      this.updateWeight();
      this.saveState();
      
      return slots;
    } catch (error) {
      console.error('Failed to fetch inventory:', error);
      
      // Return cached inventory
      if (this.state.slots) {
        console.warn('Using cached inventory data');
        return this.state.slots;
      }
      
      throw error;
    }
  }

  // Get all inventory slots (local cache)
  getSlots() {
    return this.state.slots;
  }

  // Get inventory stats
  getStats() {
    return {
      currentWeight: this.state.currentWeight,
      maxWeight: this.state.maxWeight,
      weightPercent: Math.round((this.state.currentWeight / this.state.maxWeight) * 100),
      slotsUsed: this.state.slots.filter(slot => slot !== null).length,
      totalSlots: MAX_SLOTS
    };
  }

  // Get equipped items
  getEquipped() {
    return this.state.equipped;
  }

  // Add item to inventory (DISABLED - API handles inventory management)
  addItem(itemId, quantity = 1, preferredSlot = null) {
    console.warn('addItem method disabled - API handles inventory management');
    throw new Error('Use API endpoints for inventory management');
    // Disabled - API handles all inventory operations
    return false;
  }

  // Remove item from inventory
  removeItem(slotIndex, quantity = null) {
    if (slotIndex < 0 || slotIndex >= this.state.slots.length) {
      throw new Error('Slot inválido');
    }

    const slot = this.state.slots[slotIndex];
    if (!slot) {
      throw new Error('El slot está vacío');
    }

    const removeQuantity = quantity !== null ? quantity : slot.quantity;
    
    if (removeQuantity >= slot.quantity) {
      // Remove entire stack
      this.state.slots[slotIndex] = null;
    } else {
      // Remove partial quantity
      slot.quantity -= removeQuantity;
    }

    this.updateWeight();
    this.saveState();
    return removeQuantity;
  }

  // Use item (consume)
  async useItem(itemId, quantity = 1) {
    try {
      const response = await api.useInventoryItem(itemId, quantity);
      
      // Refresh inventory after use
      await this.getInventory();
      
      // Show effects notification
      const playerStore = window.Alpine?.store('app');
      if (playerStore) {
        // Refresh player data to get updated stats
        await playerStore.refreshPlayer();
        
        // Show item used notification
        playerStore.addNotification(
          response.message, 
          'success'
        );
      }

      console.log(`Used item: ${response.message}`);
      return response;
      
    } catch (error) {
      console.error('Failed to use item:', error);
      throw new Error(error.data?.message || 'No se pudo usar el item');
    }
  }

  // Equip item (DISABLED - API handles equipment)
  equipItem(slotIndex) {
    console.warn('equipItem method disabled - API handles equipment management');
    throw new Error('Use API endpoints for equipment management');
    // const slot = this.state.slots[slotIndex];
    // if (!slot) {
    //   throw new Error('El slot está vacío');
    // }
    //
    // const itemData = inventoryData.items[slot.itemId];
    // if (!itemData || itemData.category !== 'equipment') {
    //   throw new Error('Este item no se puede equipar');
    // }

    // Disabled - API handles equipment operations
    return null;
  }

  // Unequip item (DISABLED - API handles equipment)
  unequipItem(equipSlot) {
    console.warn('unequipItem method disabled - API handles equipment management');
    throw new Error('Use API endpoints for equipment management');
    // Disabled - API handles equipment operations
    return null;
  }

  // Move item between slots
  moveItem(fromSlot, toSlot) {
    if (fromSlot === toSlot) return false;
    
    const fromItem = this.state.slots[fromSlot];
    const toItem = this.state.slots[toSlot];
    
    if (!fromItem) return false;

    // Swap items
    this.state.slots[fromSlot] = toItem;
    this.state.slots[toSlot] = fromItem;
    
    this.saveState();
    return true;
  }

  // Find first empty slot
  findEmptySlot() {
    for (let i = 0; i < this.state.slots.length; i++) {
      if (this.state.slots[i] === null) {
        return i;
      }
    }
    return -1;
  }

  // Update total weight (simplified for API)
  updateWeight() {
    // Weight calculation is now handled by API
    this.state.currentWeight = 0;
  }

  // Get item data by ID (API usage)
  getItemData(itemId) {
    // Items are now fetched from API
    return null;
  }

  // Get rarity data (API usage)
  getRarityData(rarity) {
    const rarities = {
      common: { color: '#64748b', name: 'Común' },
      uncommon: { color: '#10b981', name: 'No común' },
      rare: { color: '#3b82f6', name: 'Raro' },
      epic: { color: '#8b5cf6', name: 'Épico' },
      legendary: { color: '#f59e0b', name: 'Legendario' }
    };
    return rarities[rarity] || rarities.common;
  }

  // Clear inventory (for testing)
  clear() {
    this.state = { ...defaultState };
    this.saveState();
    console.log('Inventory cleared');
  }

  // Get inventory value (API usage)
  getTotalValue() {
    // Value calculation now handled by API
    return 0;
  }
}

// Export singleton instance
export const inventoryService = new InventoryService();

// Export class for testing
export { InventoryService };